/**
 * Camera. First person by default at the TPDM driver eye height, with a chase
 * view for orientation.
 *
 * The eye height is the single most consequential rendering decision in the
 * project. At 1.2 m the lower half of the frame is road surface, which is why
 * `docs/RENDERING.md` puts road material and marking sharpness at the top of
 * the effort list and distant geometry at the bottom.
 */
import { MathUtils, PerspectiveCamera, Vector3 } from 'three';
import { EYE_HEIGHT_M, ROAD, VEHICLE } from '../core/spec';
import type { CarState } from './arcadeCar';

export type CameraMode = 'first' | 'chase';

/** Driver's eye, offset from the car's centre. Right-hand drive. */
const EYE_FORWARD = 0.35;
const EYE_RIGHT = 0.35;

const CHASE_BACK = 7.0;
const CHASE_UP = 3.0;

/** Damping rates, per second. */
const POS_DAMP = { first: 30, chase: 6 } as const;
const LOOK_DAMP = { first: 24, chase: 8 } as const;

export class ChaseCamera {
  readonly camera: PerspectiveCamera;
  mode: CameraMode = 'first';

  private readonly pos = new Vector3();
  private readonly look = new Vector3();
  private readonly tmpPos = new Vector3();
  private readonly tmpLook = new Vector3();
  private initialised = false;

  constructor(aspect: number) {
    // 60 deg vertical FOV: wide enough to feel like a windscreen, narrow enough
    // that the road doesn't stretch at the frame edges on a phone.
    this.camera = new PerspectiveCamera(60, aspect, 0.25, 3000);
    // Far plane sits beyond the fog, so nothing ever pops in at the horizon.
  }

  setAspect(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  toggle(): void {
    this.mode = this.mode === 'first' ? 'chase' : 'first';
  }

  update(car: CarState, dt: number): void {
    const c = Math.cos(car.heading);
    const s = Math.sin(car.heading);
    const fwd = { x: s, z: -c };
    const right = { x: c, z: s };

    if (this.mode === 'first') {
      this.tmpPos.set(
        car.x + fwd.x * EYE_FORWARD + right.x * EYE_RIGHT,
        car.y + EYE_HEIGHT_M,
        car.z + fwd.z * EYE_FORWARD + right.z * EYE_RIGHT,
      );
      // Look one stopping-sight-distance ahead (TPDM 70 m at 50 km/h) so the
      // horizon stays put while the body pitches over crests.
      const ahead = ROAD.SIGHT_DISTANCE_DESIRABLE_M;
      this.tmpLook.set(
        car.x + fwd.x * ahead,
        car.y + EYE_HEIGHT_M - Math.sin(car.pitch) * ahead * 0.35,
        car.z + fwd.z * ahead,
      );
    } else {
      this.tmpPos.set(
        car.x - fwd.x * CHASE_BACK,
        car.y + CHASE_UP,
        car.z - fwd.z * CHASE_BACK,
      );
      this.tmpLook.set(
        car.x + fwd.x * VEHICLE.LENGTH_M,
        car.y + 1.0,
        car.z + fwd.z * VEHICLE.LENGTH_M,
      );
    }

    if (!this.initialised) {
      this.pos.copy(this.tmpPos);
      this.look.copy(this.tmpLook);
      this.initialised = true;
    } else {
      const kp = 1 - Math.exp(-POS_DAMP[this.mode] * dt);
      const kl = 1 - Math.exp(-LOOK_DAMP[this.mode] * dt);
      this.pos.lerp(this.tmpPos, kp);
      this.look.lerp(this.tmpLook, kl);
    }

    this.camera.position.copy(this.pos);
    this.camera.lookAt(this.look);
    // Roll the view with the car, heavily reduced. Full body roll on a 2.5%
    // crossfall reads as a wobbly camera; none at all loses the crown entirely.
    this.camera.rotateZ(MathUtils.clamp(-car.roll * 0.35, -0.15, 0.15));
  }
}
